import React, { createContext, useContext, useEffect, useRef } from 'react'

type EventCallback = (payload?: any) => void

interface EventBus {
  subscribe: (event: string, callback: EventCallback) => void
  unsubscribe: (event: string, callback: EventCallback) => void
  emit: (event: string, payload?: any) => void
}

const EVENT_BUS_KEY = '__GLOBAL_EVENT_BUS__'

export function getGlobalEventBus(): EventBus {
  if (!(window as any)[EVENT_BUS_KEY]) {
    const listeners: Record<string, Set<EventCallback>> = {}

    ;(window as any)[EVENT_BUS_KEY] = {
      subscribe(event: string, callback: EventCallback) {
        if (!listeners[event]) listeners[event] = new Set()
        listeners[event].add(callback)
      },
      unsubscribe(event: string, callback: EventCallback) {
        listeners[event]?.delete(callback)
      },
      emit(event: string, payload?: any) {
        listeners[event]?.forEach((cb) => cb(payload))
      },
    }
  }
  return (window as any)[EVENT_BUS_KEY]
}

const EventContext = createContext<EventBus | null>(null)

export const useEventBus = () => {
  const ctx = useContext(EventContext)
  if (!ctx) throw new Error('useEventBus must be used within EventProvider')
  return ctx
}

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const eventBusRef = useRef<EventBus>()

  if (!eventBusRef.current) {
    eventBusRef.current = getGlobalEventBus()
  }

  useEffect(() => {
    // No cleanup needed, global instance
  }, [])

  return (
    <EventContext.Provider value={eventBusRef.current}>
      {children}
    </EventContext.Provider>
  )
}
